/**
 * The table-agnostic half of list filtering.
 *
 * A filter is a description of a predicate the database applies, and nothing
 * else. There is deliberately no way to express one that runs over rows after
 * they are fetched: a paginated list only ever holds one page, so a filter the
 * query cannot see reaches neither the rows on the other pages nor the count
 * the pager is drawn from.
 *
 * A table supplies a registry — one entry per filter — and this module reads
 * the filters off a URL, writes them back to it, compiles them into PostgREST
 * predicates, and evaluates them in memory for the unpaginated consumers.
 */

export type ConditionOp = 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte';

export type Condition = {
  column: string;
  op: ConditionOp;
  value: string | number | boolean;
};

/** A predicate on a column of the table being listed. */
export type ColumnPredicate = { kind: 'column' } & Condition;

/** A predicate on a related table, applied as an inner join. */
export type EmbeddedPredicate = {
  kind: 'embedded';
  table: string;
  /** Columns to select from the embed. */
  select: string;
  /**
   * The name the embed keeps in the response. Leave it unset for a throwaway
   * join — the compiler assigns a unique alias, which is what lets the same
   * relation be joined once per filter, each matching a different row. Set it
   * only where something reads the embedded rows off the result by name.
   */
  alias?: string;
  /** ANDed together, and so satisfied by a single joined row. */
  conditions: Condition[];
};

export type QueryPredicate = ColumnPredicate | EmbeddedPredicate;

/**
 * How one filter reads from a URL, writes back to it, reaches the database,
 * and — for lists that hold every row at once — answers in memory.
 */
export type FilterDef<Filter, Row, Context> = {
  /** The search param this filter reads and writes. */
  param: string;
  /** Parses the param's raw value. Anything malformed is dropped, never thrown. */
  parse: (raw: string, context: Context) => Filter[];
  /** Returns null to clear the param. */
  serialize: (filters: Filter[]) => string | null;
  toPredicate: (filter: Filter) => QueryPredicate;
  matches: (filter: Filter, row: Row) => boolean;
};

/** One entry per member of the filter union, keyed by its `id`. */
export type FilterRegistry<Filter extends { id: string }, Row, Context> = {
  [Id in Filter['id']]: FilterDef<Extract<Filter, { id: Id }>, Row, Context>;
};

export type CompiledFilters = {
  /** Embedded resources to add to the select, in order. */
  embeds: string[];
  /** Predicates to apply, their paths already qualified by any embed alias. */
  conditions: { path: string; op: ConditionOp; value: Condition['value'] }[];
};

/**
 * Looks an entry up by a filter's own id. The registry is keyed by that id, so
 * the entry always matches the filter, which the type system cannot see across
 * a union.
 */
function defFor<Filter extends { id: string }, Row, Context>(
  registry: FilterRegistry<Filter, Row, Context>,
  id: Filter['id']
): FilterDef<Filter, Row, Context> {
  return registry[id] as unknown as FilterDef<Filter, Row, Context>;
}

/** The params a registry owns, for clearing every filter at once. */
export function filterParams<Filter extends { id: string }, Row, Context>(
  registry: FilterRegistry<Filter, Row, Context>
): string[] {
  return Object.values(
    registry as unknown as Record<string, FilterDef<Filter, Row, Context>>
  ).map((def) => def.param);
}

/**
 * Reads every filter a registry knows about off a set of search params. Filters
 * come back in registry order, so the same URL always produces the same array —
 * which is what makes the compiled query and its cache key stable.
 */
export function parseFilters<Filter extends { id: string }, Row, Context>(
  searchParams: Record<string, string | undefined>,
  registry: FilterRegistry<Filter, Row, Context>,
  context: Context
): Filter[] {
  const filters: Filter[] = [];
  for (const id of Object.keys(registry) as Filter['id'][]) {
    const def = defFor(registry, id);
    const raw = searchParams[def.param];
    if (raw === undefined || raw === '') continue;
    filters.push(...def.parse(raw, context));
  }
  return filters;
}

/**
 * The param values that represent this set of filters. Every param the registry
 * owns is present — one with no active filter maps to null, which is what
 * clears it from the URL.
 */
export function serializeFilters<Filter extends { id: string }, Row, Context>(
  filters: Filter[],
  registry: FilterRegistry<Filter, Row, Context>
): Record<string, string | null> {
  const params: Record<string, string | null> = {};
  for (const id of Object.keys(registry) as Filter['id'][]) {
    const def = defFor(registry, id);
    const mine = filters.filter((filter) => filter.id === id);
    params[def.param] = mine.length > 0 ? def.serialize(mine) : null;
  }
  return params;
}

/**
 * A stable string for this set of filters, for use in a cache key. Params are
 * sorted so the key does not depend on the order the filters were applied in.
 */
export function filterSearchString<Filter extends { id: string }, Row, Context>(
  filters: Filter[],
  registry: FilterRegistry<Filter, Row, Context>
): string {
  const params = serializeFilters(filters, registry);
  return Object.entries(params)
    .filter(([, value]) => value !== null)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([param, value]) => `${param}=${encodeURIComponent(value as string)}`)
    .join('&');
}

/**
 * Compiles filters into the pieces of a PostgREST query. Embedded filters are
 * joined once each: two filters on the same relation get their own alias, so
 * each is satisfied by a different row rather than by one row having to satisfy
 * both.
 */
export function compileFilters<Filter extends { id: string }, Row, Context>(
  filters: Filter[],
  registry: FilterRegistry<Filter, Row, Context>
): CompiledFilters {
  const embeds: string[] = [];
  const conditions: CompiledFilters['conditions'] = [];
  let joined = 0;

  for (const filter of filters) {
    const predicate = defFor(registry, filter.id).toPredicate(filter);

    if (predicate.kind === 'column') {
      conditions.push({
        path: predicate.column,
        op: predicate.op,
        value: predicate.value
      });
      continue;
    }

    const alias = predicate.alias ?? `f${joined++}`;
    embeds.push(
      alias === predicate.table
        ? `${predicate.table}!inner(${predicate.select})`
        : `${alias}:${predicate.table}!inner(${predicate.select})`
    );
    for (const condition of predicate.conditions) {
      conditions.push({
        path: `${alias}.${condition.column}`,
        op: condition.op,
        value: condition.value
      });
    }
  }

  return { embeds, conditions };
}

/**
 * Evaluates filters against a row already in memory. For lists that hold every
 * row at once — never as a substitute for the compiled query on a paginated
 * one.
 */
export function matchesFilters<Filter extends { id: string }, Row, Context>(
  row: Row,
  filters: Filter[],
  registry: FilterRegistry<Filter, Row, Context>
): boolean {
  return filters.every((filter) =>
    defFor(registry, filter.id).matches(filter, row)
  );
}
